import React, { useState } from 'react';
import { Button, Input, Modal, Space, Table, Typography, message } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { ExpV2UserList, ExpV2UserListDetail } from '@/types/experimentsV2';
import { apiErrorMessage } from '@/utils/apiError';
import { LIST_MAX, parseUserIds } from './actions';
import { istTime } from './display';

export interface ListsPanelProps {
  lists: ExpV2UserList[];
  canCreate: boolean;
  canEdit: boolean;
  onCreate: (name: string, userIds: string[]) => Promise<void>;
  onUpdate: (id: string, body: { name?: string; userIds?: string[] }) => Promise<void>;
  loadList: (id: string) => Promise<ExpV2UserListDetail>;
}

interface Editing {
  id: string | null;
  name: string;
  text: string;
  /** Ids as loaded, so an untouched paste box sends no replacement. */
  original: string;
}

/** Named sets of player ids a test can target (targeting.userListId). */
export const ListsPanel: React.FC<ListsPanelProps> = (p) => {
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);

  const openEdit = async (l: ExpV2UserList) => {
    try {
      const d = await p.loadList(l.id);
      const text = d.userIds.join('\n');
      setEditing({ id: l.id, name: d.name, text, original: text });
    } catch (e) {
      message.error(apiErrorMessage(e, 'Could not load the list'));
    }
  };

  const parsed = editing ? parseUserIds(editing.text) : null;
  const idsChanged = editing ? editing.text !== editing.original : false;
  const problem =
    !editing || editing.name.trim() === ''
      ? 'Name the list'
      : (editing.id === null || idsChanged) && parsed?.problem
        ? parsed.problem
        : null;

  const save = async () => {
    if (!editing || problem || !parsed) return;
    setSaving(true);
    try {
      if (editing.id === null) {
        await p.onCreate(editing.name.trim(), parsed.ids);
      } else {
        await p.onUpdate(editing.id, {
          name: editing.name.trim(),
          ...(idsChanged ? { userIds: parsed.ids } : {}),
        });
      }
      setEditing(null);
    } catch {
      // The page shows the server's message; the dialog stays open to fix it.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} data-section="lists">
      <Space wrap>
        {p.canCreate && (
          <Button
            icon={<PlusOutlined />}
            data-create-list
            onClick={() => setEditing({ id: null, name: '', text: '', original: '' })}
          >
            Create list
          </Button>
        )}
        <Typography.Text type="secondary">
          Edits reach running tests within about 10 minutes; flags can take up to 6 hours.
        </Typography.Text>
      </Space>
      <Table<ExpV2UserList>
        size="small"
        rowKey="id"
        dataSource={p.lists}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        columns={[
          { title: 'Name', dataIndex: 'name' },
          { title: 'Players', dataIndex: 'size' },
          { title: 'Updated (IST)', dataIndex: 'updatedAt', render: (v: string) => istTime(v) },
          ...(p.canEdit
            ? [
                {
                  title: '',
                  key: 'edit',
                  render: (_: unknown, l: ExpV2UserList) => (
                    <Button size="small" icon={<EditOutlined />} onClick={() => void openEdit(l)}>
                      Edit
                    </Button>
                  ),
                },
              ]
            : []),
        ]}
      />
      <Modal
        open={editing !== null}
        title={editing?.id ? `Edit ${editing.name}` : 'Create list'}
        okText="Save"
        confirmLoading={saving}
        okButtonProps={{ disabled: problem !== null }}
        onCancel={() => setEditing(null)}
        onOk={() => void save()}
      >
        {editing && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              id="expv2-list-name"
              placeholder="Name"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            />
            <Input.TextArea
              id="expv2-list-ids"
              rows={8}
              placeholder={`Player ids, one per line or separated by commas (${LIST_MAX.toLocaleString()} at most)`}
              value={editing.text}
              onChange={(e) => setEditing({ ...editing, text: e.target.value })}
            />
            <Typography.Text type="secondary">
              {parsed?.ids.length ?? 0} ids
              {editing.id !== null && !idsChanged ? ' (unchanged)' : ''}
            </Typography.Text>
            {problem && <Typography.Text type="danger">{problem}</Typography.Text>}
          </Space>
        )}
      </Modal>
    </Space>
  );
};
